import { useCallback, useState } from 'react'
import { Icon } from '@/components/ui/icon'
import { SHADER_PROVIDER_LABELS, type ShaderPromptEntry } from '@/lib/canvas/shader/authoring/types'

interface ShaderPromptHistoryProps {
  entries: readonly ShaderPromptEntry[]
  /** Puts a past prompt back in the node's prompt box. */
  onReuse: (prompt: string) => void
  /** Puts that revision's code back on this shader. Absent entries cannot restore. */
  onRestore?: (entry: ShaderPromptEntry) => void
  /** The code running now, so compare can say whether a revision still matches. */
  currentCode?: string
}

interface EntryProps {
  entry: ShaderPromptEntry
  onReuse: (prompt: string) => void
  onRestore?: (entry: ShaderPromptEntry) => void
  currentCode?: string
}

function formatWhen(at: string | undefined): string {
  if (!at) return ''
  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function HistoryEntry({ entry, onReuse, onRestore, currentCode }: EntryProps) {
  const [open, setOpen] = useState(false)
  const handleReuse = useCallback(() => onReuse(entry.prompt), [onReuse, entry.prompt])
  const handleRestore = useCallback(() => onRestore?.(entry), [onRestore, entry])
  const handleCompare = useCallback(() => setOpen((value) => !value), [])
  const when = formatWhen(entry.at)
  const canRestore = Boolean(entry.code && onRestore)
  const canCompare = Boolean(entry.code || entry.error)
  const matches = entry.code !== undefined && entry.code === currentCode

  return (
    <li className="group flex flex-col gap-0.5 rounded-md border border-border px-2 py-1.5">
      <div className="flex items-start gap-1.5">
        <span className="flex-1 text-xs leading-snug">{entry.prompt}</span>
        <button
          type="button"
          onClick={handleReuse}
          className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
          aria-label="Use this prompt again"
          title="Use again"
        >
          <Icon name="corner-down-left" className="h-3 w-3" />
        </button>
      </div>
      {entry.summary && <span className="text-[10px] leading-snug text-muted-foreground">{entry.summary}</span>}
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70">
        {entry.mode === 'edit' ? 'Edit' : 'Create'} · {SHADER_PROVIDER_LABELS[entry.provider] ?? entry.provider} · {entry.model}
        {when ? ` · ${when}` : ''}
        {entry.error ? ' · Failed' : ''}
      </span>
      {(canRestore || canCompare) && (
        <div className="mt-1 flex gap-2">
          {canRestore && (
            <button type="button" onClick={handleRestore} className="text-[10px] text-foreground underline decoration-dotted underline-offset-2" aria-label="Restore this shader">
              Restore
            </button>
          )}
          {canCompare && (
            <button type="button" onClick={handleCompare} className="text-[10px] text-foreground underline decoration-dotted underline-offset-2" aria-expanded={open} aria-label="Compare this shader">
              Compare
            </button>
          )}
        </div>
      )}
      {open && (
        <div role="region" aria-label="Shader revision" className="mt-1 rounded bg-muted/50 p-1.5">
          {entry.code ? (
            <>
              <p className="text-[10px] text-muted-foreground">{matches ? 'This is the code running now.' : 'This revision differs from the code running now.'}</p>
              <pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] leading-snug">{entry.code}</pre>
            </>
          ) : (
            <p className="text-[10px] text-red-700 dark:text-red-300">{entry.error}</p>
          )}
        </div>
      )}
    </li>
  )
}

/** Prompts applied to this node, newest first, with what each one did. */
export function ShaderPromptHistory({ entries, onReuse, onRestore, currentCode }: ShaderPromptHistoryProps) {
  if (entries.length === 0) {
    return <p className="px-2 py-2 text-[10px] text-muted-foreground">Prompts you apply on the node show up here.</p>
  }
  return (
    <ol className="nodrag nowheel nopan flex w-full flex-col gap-1.5 px-2 py-2" aria-label="Prompt history">
      {[...entries].reverse().map((entry) => (
        <HistoryEntry key={entry.id} entry={entry} onReuse={onReuse} onRestore={onRestore} currentCode={currentCode} />
      ))}
    </ol>
  )
}
